import 'react-app-polyfill/stable';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import 'core-js/actual/promise/finally';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { ga } from '@rsos/capstone';
import { createBaseGainsightProvider } from '@rsos/capstone';
import '@rsos/capstone/src/overridePublicPath';
import Layout from '@rsos/components/capstone/Layout';
import { FlagProvider } from '@rsos/flags';
import { gspx } from '@rsos/gainsight';
import {
  getAmplitudeApiKey,
  getAmplitudeProxyOrigin,
} from '@rsos/utils/metaTags';
import { initDatadogFlags, OpenFeatureProvider } from '@rsos/utils/openFeature';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import '@rsos/utils/sentry';
import App, { topLevelRoutes } from './app/App';
import ErrorBoundary from './components/capstoneLegacy/Common/ErrorBoundary';
import './scss/index.scss';
import * as serviceWorker from './serviceWorker';
import { store, persistor } from './store';

ga.initialize(store);

// Initialize Gainsight with data provider and error handling
const dataProvider = {
  ...createBaseGainsightProvider(store),
  // Authentication app doesn't handle call data
  getSelectedCallData: () => null,
};
gspx.initialize(dataProvider, {
  captureError: captureExceptionWithScope,
  amplitude: {
    apiKey: getAmplitudeApiKey(),
    proxyOrigin: getAmplitudeProxyOrigin(),
    // Login/SSO screens redirect away quickly; skip G&S to avoid aborted decide fetches.
    enableGuides: false,
  },
});

const container = document.getElementById('root');

const root = createRoot(container);

// Register the Datadog OpenFeature provider so flags (e.g. the auth-footer
// version) resolve via OpenFeature. No-ops (default provider) without creds.
initDatadogFlags();

root.render(
  <OpenFeatureProvider>
    <Provider store={store}>
      <FlagProvider>
        <PersistGate loading={'Loading'} persistor={persistor}>
          <BrowserRouter>
            <Layout
              appPaths={topLevelRoutes.map(route => route.path)}
              persistor={persistor}
            >
              <ErrorBoundary>
                <App />
              </ErrorBoundary>
            </Layout>
          </BrowserRouter>
        </PersistGate>
      </FlagProvider>
    </Provider>
  </OpenFeatureProvider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
